import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, ShieldCheck } from "lucide-react";
import {
  CURRICULUM_LABELS,
  ANCHOR_TIERS,
  SHOWCASE_TIERS,
  equivalentsFor,
  tariffCombos,
} from "@/lib/gradeEquivalency";

const CURRICULA = ["a_level", "ib", "atar", "gpa", "sat", "act", "cbse", "isc", "ap_composite", "abitur", "french_bac", "hkdse", "suneung", "eju", "ucas"];

// Placeholder + hint per curriculum — the calculator's input box changes
// shape as the source curriculum changes, so keep the copy inline.
const INPUT_META = {
  a_level: { placeholder: "e.g. A*AA", type: "text", hint: "Three grades, e.g. A*AA, AAB, BBB." },
  ib:      { placeholder: "e.g. 42",    type: "number", min: 20, max: 45, step: 1, hint: "IB total (24–45)." },
  atar:    { placeholder: "e.g. 88.50", type: "number", min: 0,  max: 99.95, step: 0.05, hint: "Australian Tertiary Admission Rank (0–99.95)." },
  gpa:     { placeholder: "e.g. 3.85",  type: "number", min: 0,  max: 4.0, step: 0.01, hint: "US 4.0 scale (unweighted)." },
  sat:     { placeholder: "e.g. 1450",  type: "number", min: 400, max: 1600, step: 10, hint: "SAT total (Math + EBRW), 400–1600 in 10-point steps." },
  act:     { placeholder: "e.g. 32",    type: "number", min: 1, max: 36, step: 1, hint: "ACT composite (1–36)." },
  cbse:    { placeholder: "e.g. 92",    type: "number", min: 0,  max: 100, step: 0.5, hint: "CBSE overall percentage (0–100)." },
  isc:     { placeholder: "e.g. 90",    type: "number", min: 0,  max: 100, step: 0.5, hint: "ISC / CISCE overall % — slightly stricter marking than CBSE." },
  ap_composite: { placeholder: "e.g. 4.2", type: "number", min: 1.0, max: 5.0, step: 0.05, hint: "Average score across your AP exams (1.0–5.0). Assumes ≥3 AP exams sat." },
  abitur:  { placeholder: "e.g. 1.7",   type: "number", min: 1.0, max: 4.0, step: 0.1, hint: "Abitur average — 1.0 is best, 4.0 is the pass floor." },
  french_bac: { placeholder: "e.g. 15", type: "number", min: 0,  max: 20, step: 0.5, hint: "Baccalauréat overall out of 20 (10 pass, 12 AB, 14 B, 16 TB)." },
  hkdse:   { placeholder: "e.g. 28",    type: "number", min: 0,  max: 35, step: 1, hint: "Sum of your best-5 subject scores (5**=7, 5*=6, 5=5, 4=4, …)." },
  suneung: { placeholder: "e.g. 90",    type: "number", min: 0,  max: 100, step: 1, hint: "Korean CSAT (수능) national-percentile average across your top-3 sections." },
  eju:     { placeholder: "e.g. 690",   type: "number", min: 0,  max: 800, step: 5, hint: "Japan EJU top-3 raw point sum: Japanese (/400) + two of Math/Science/Japan & the World (/200 each). Max 800." },
  ucas:    { placeholder: "e.g. 144",   type: "number", min: 0,  max: 200, step: 1, hint: "UCAS Tariff points — A-levels earn 56 (A*) / 48 (A) / 40 (B) / 32 (C) / 24 (D) / 16 (E); IB HL earns 56 (7) / 48 (6) / 32 (5) / 24 (4) / 12 (3)." },
};

const OUTPUT_ORDER = ["a_level", "ib", "atar", "gpa", "sat", "act", "cbse", "isc", "ap_composite", "abitur", "french_bac", "hkdse", "suneung", "eju", "ucas"];

const OUTPUT_META = {
  a_level:      { label: "A Levels (UK)",         unit: "" },
  ib:           { label: "IB Diploma",            unit: " / 45" },
  atar:         { label: "ATAR (Australia)",      unit: "" },
  gpa:          { label: "GPA (US, 4.0)",         unit: " / 4.0" },
  sat:          { label: "SAT total (US)",        unit: " / 1600" },
  act:          { label: "ACT composite (US)",    unit: " / 36" },
  cbse:         { label: "CBSE (India)",          unit: "%" },
  isc:          { label: "ISC / CISCE (India)",   unit: "%" },
  ap_composite: { label: "AP composite (US)",     unit: " / 5.0" },
  abitur:       { label: "Abitur (Germany)",      unit: " (1.0 best)" },
  french_bac:   { label: "Bac (France)",          unit: " / 20" },
  hkdse:        { label: "HKDSE best-5 (HK)",     unit: " / 35" },
  suneung:      { label: "Suneung / CSAT (Korea)", unit: " %" },
  eju:          { label: "EJU top-3 (Japan)",     unit: " / 800" },
  ucas:         { label: "UCAS points",           unit: " pts" },
};


export default function GradeCalculator() {
  const [curriculum, setCurriculum] = useState("a_level");
  const [value, setValue] = useState("A*AA");

  const meta = INPUT_META[curriculum];

  const result = useMemo(() => {
    return equivalentsFor(curriculum, value);
  }, [curriculum, value]);

  const onCurriculumChange = (next) => {
    // Pre-fill a sensible default so the equivalents table never stays empty.
    setCurriculum(next);
    const defaults = {
      a_level: "A*AA", ib: 38, atar: 90, gpa: 3.7, cbse: 90,
      isc: 88, ap_composite: 4.2, abitur: 1.7, french_bac: 15, hkdse: 27,
      suneung: 90, eju: 690, sat: 1450, act: 32, ucas: 144,
    };
    setValue(defaults[next]);
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <div className="label-uppercase text-stone-500 mb-2 flex items-center gap-2">
        <Calculator className="h-3 w-3" />
        Grade equivalency calculator
      </div>
      <h1 className="font-editorial text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
        Convert your grade across curricula.
      </h1>
      <p className="mt-3 text-stone-600 max-w-2xl leading-snug">
        A-Level to IB, IB to ATAR, ATAR to GPA. Type once — see the
        equivalent in every other curriculum, live. Handy when a
        university publishes its offer in an unfamiliar system.
      </p>

      {/* --- Main calculator card ------------------------------------------ */}
      <Card
        className="mt-8 p-6 grid gap-6 md:grid-cols-[minmax(260px,340px)_1fr] items-start"
        data-testid="grade-calculator-card"
      >
        {/* left — input */}
        <div>
          <Label className="label-uppercase">Your curriculum</Label>
          <Select value={curriculum} onValueChange={onCurriculumChange}>
            <SelectTrigger
              className="mt-1"
              data-testid="grade-calc-curriculum-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRICULA.map((c) => (
                <SelectItem key={c} value={c}>{CURRICULUM_LABELS[c]}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="mt-4">
            <Label className="label-uppercase">Your grade</Label>
            <Input
              data-testid="grade-calc-value-input"
              value={value ?? ""}
              onChange={(e) => setValue(curriculum === "a_level"
                ? e.target.value.toUpperCase()
                : e.target.value)}
              placeholder={meta.placeholder}
              type={meta.type}
              min={meta.min}
              max={meta.max}
              step={meta.step}
              className="mt-1 font-mono"
            />
            <p className="mt-1 text-[11px] text-stone-500">{meta.hint}</p>
          </div>

          <div className="mt-4 text-[11px] font-underground-sc tracking-wider text-stone-500">
            {result ? `Tier · ${result.input.tier}` : "· enter a grade ·"}
          </div>
        </div>

        {/* right — outputs */}
        <div>
          <Label className="label-uppercase">Equivalents</Label>
          {result ? (
            <ul
              className="mt-1 grid gap-2 sm:grid-cols-2"
              data-testid="grade-calc-equivalents"
            >
              {OUTPUT_ORDER
                .filter((k) => k !== curriculum) // don't echo the input curriculum
                .map((k) => (
                  <li
                    key={k}
                    className="flex items-baseline justify-between gap-3 border border-stone-200 rounded-md px-3 py-2 bg-white"
                    data-testid={`grade-calc-out-${k}`}
                  >
                    <span className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                      {OUTPUT_META[k].label}
                    </span>
                    <span className="font-editorial text-lg text-stone-900">
                      {result.equivalents[k]}
                      <span className="text-stone-500 text-xs font-normal">
                        {OUTPUT_META[k].unit}
                      </span>
                    </span>
                  </li>
                ))}
            </ul>
          ) : (
            <div
              className="mt-1 text-sm text-stone-500 border border-dashed border-stone-300 rounded-md px-4 py-6"
              data-testid="grade-calc-empty"
            >
              Enter a grade to see the equivalents.
            </div>
          )}

          {/* Reverse-tariff: only when the student is entering UCAS
              Tariff points. Answers "which grades hit this offer?" —
              the most common UK admissions question once a student has
              been given a points-based conditional offer. */}
          {curriculum === "ucas" && result && (
            <TariffCombosPanel target={Number(value)} />
          )}

          <p className="mt-3 text-[11px] text-stone-500 leading-snug max-w-lg">
            Mappings are indicative — sourced from UCAS Points Calculator,
            IBO conversion tables, UAC/TAC ATAR guidance and common US-4.0
            equivalency tables. Individual course pages remain authoritative.
          </p>
        </div>
      </Card>

      {/* --- WUA+ attestation upsell ---------------------------------- */}
      <Card
        className="mt-6 p-5 sm:p-6 border-stone-200 bg-gradient-to-br from-[#F5F1E6] to-white"
        data-testid="grade-calc-wua-plus-upsell"
      >
        <div className="flex flex-wrap items-start gap-4">
          <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#4C1D95] text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="label-uppercase text-[#4C1D95] mb-1">
              Need it attested?
            </div>
            <h3 className="font-editorial text-xl sm:text-2xl leading-tight text-stone-900">
              If you need your equivalency attested officially,{" "}
              <span className="text-[#4C1D95]">
                WhichUniversityAdvisor.com Plus
              </span>{" "}
              will help.
            </h3>
            <p className="mt-2 text-sm text-stone-600 leading-snug max-w-xl">
              Our counsellors issue a signed, dated equivalency letter you
              can submit alongside your application — accepted by admissions
              offices that ask for an official conversion of your Abitur,
              Baccalauréat, HKDSE, ISC, ATAR or AP composite into A-Level, IB
              or GPA terms.
            </p>
          </div>
          <div className="w-full sm:w-auto shrink-0">
            <Link
              to="/wua-plus?intent=attestation&source=grade-calculator"
              className="inline-flex w-full sm:w-auto justify-center items-center gap-1.5 text-[12px] font-underground-sc tracking-wider text-white bg-[#4C1D95] px-4 py-2.5 rounded-full hover:bg-[#3d1778] transition-colors"
              data-testid="grade-calc-wua-plus-cta"
            >
              Get official attestation
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </Card>

      {/* --- Showcase strip ------------------------------------------------- */}
      <div className="mt-12" data-testid="grade-calc-showcase">
        <div className="label-uppercase text-stone-500 mb-3">
          What universities typically expect at each tier
        </div>
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full min-w-[1000px] text-sm">
            <thead>
              <tr className="text-left text-[10px] font-underground-sc tracking-wider text-stone-500 border-b border-stone-200">
                <th className="pb-2 pr-4">Tier</th>
                <th className="pb-2 pr-4">A-Level</th>
                <th className="pb-2 pr-4">IB</th>
                <th className="pb-2 pr-4">ATAR</th>
                <th className="pb-2 pr-4">GPA</th>
                <th className="pb-2 pr-4">SAT</th>
                <th className="pb-2 pr-4">ACT</th>
                <th className="pb-2 pr-4">CBSE</th>
                <th className="pb-2 pr-4">ISC</th>
                <th className="pb-2 pr-4">AP</th>
                <th className="pb-2 pr-4">Abitur</th>
                <th className="pb-2 pr-4">Bac</th>
                <th className="pb-2 pr-4">HKDSE</th>
                <th className="pb-2 pr-4">Suneung</th>
                <th className="pb-2 pr-4">EJU</th>
                <th className="pb-2 pr-4">UCAS</th>
              </tr>
            </thead>
            <tbody>
              {SHOWCASE_TIERS.map((row) => {
                const anchor = ANCHOR_TIERS.find((a) => a.tier === row.tier);
                if (!anchor) return null;
                return (
                  <tr
                    key={row.label}
                    className="border-b border-stone-100 last:border-b-0"
                    data-testid={`grade-calc-tier-${row.tier.replace(/\*/g, "star")}`}
                  >
                    <td className="py-2 pr-4">
                      <div className="font-editorial text-base text-stone-900">
                        {row.label}
                      </div>
                      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                        Tier · {row.tier}
                      </div>
                    </td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.tier}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.ib}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.atar}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.gpa.toFixed(2)}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.sat}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.act}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.cbse}%</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.isc}%</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.ap_composite.toFixed(2)}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.abitur.toFixed(1)}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.french_bac}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.hkdse_best5}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.suneung_pct}%</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.eju_top3}</td>
                    <td className="py-2 pr-4 font-mono text-stone-800">{anchor.ucas}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Next-step CTA -------------------------------------------------- */}
      <div className="mt-12 border-t border-stone-200 pt-8">
        <div className="label-uppercase text-stone-500 mb-2">Next step</div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-stone-700 max-w-xl leading-snug">
            Now that you know your grade in every system, run it through
            Quick Check to see how many universities you're actually a
            realistic candidate for — country by country.
          </p>
          <Link
            to="/admission"
            className="inline-flex items-center gap-1.5 text-[12px] font-underground-sc tracking-wider text-white bg-[#4C1D95] px-4 py-2 rounded-full hover:bg-[#3d1778] transition-colors"
            data-testid="grade-calc-quick-check-cta"
          >
            Try Quick Check
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}


/**
 * TariffCombosPanel — surfaces every 3-subject grade combination that
 * sums to the UCAS Tariff `target`. Exact matches on top; if none
 * exist (e.g. 125, which no clean A-level combo hits), we show the
 * closest few within ±8 points so the student still gets a useful
 * answer rather than an empty state.
 */
function TariffCombosPanel({ target }) {
  const combos = useMemo(() => {
    const exact = tariffCombos(target, { tolerance: 0 });
    if (exact.aLevel.length === 0 && exact.ib.length === 0) {
      return { ...tariffCombos(target, { tolerance: 8 }), exact: false };
    }
    return { ...exact, exact: true };
  }, [target]);

  if (!Number.isFinite(target) || target <= 0) return null;
  const trimmed = (arr) => arr.slice(0, 8);
  const anyMatches = combos.aLevel.length + combos.ib.length > 0;

  return (
    <section
      className="mt-5 rounded-xl border border-amber-200 bg-amber-50/50 p-4"
      data-testid="tariff-combos-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        <Calculator className="h-4 w-4 text-amber-700" />
        <div className="text-[11px] font-underground-sc tracking-[0.14em] text-amber-900">
          Which grades hit {target} UCAS points?
        </div>
      </div>

      {!anyMatches && (
        <div className="text-[12.5px] text-stone-600">
          No clean 3-subject combination hits exactly this tariff. Try a
          value like 112, 120, 128, 136, 144 or 152 — those line up with
          standard UK offer bands.
        </div>
      )}

      {anyMatches && (
        <>
          {!combos.exact && (
            <div className="text-[11px] text-amber-800 mb-2">
              No exact match — showing the closest combinations (±8 points).
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <TariffCol
              label="A-levels (UK)"
              rows={trimmed(combos.aLevel)}
              testId="tariff-col-alevel"
            />
            <TariffCol
              label="IB Diploma (HL subjects)"
              rows={trimmed(combos.ib)}
              testId="tariff-col-ib"
              suffix=" HL"
            />
          </div>
          <div className="text-[11px] text-stone-500 mt-3 leading-snug">
            Three-subject combinations only — UK offers almost always
            quote three A-levels or three HL IB subjects. AS-levels and
            SL/EPQ bonuses can top up the total but aren't shown here.
          </div>
        </>
      )}
    </section>
  );
}

function TariffCol({ label, rows, testId, suffix = "" }) {
  if (!rows.length) {
    return (
      <div data-testid={testId}>
        <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">{label}</div>
        <div className="text-[12px] text-stone-500 italic">No combination near this target.</div>
      </div>
    );
  }
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">{label}</div>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li
            key={`${label}-${r.letters}-${r.points}`}
            className="flex items-baseline justify-between bg-white border border-stone-200 rounded px-2.5 py-1.5"
            data-testid={`tariff-combo-${r.letters}`}
          >
            <span className="font-editorial text-base text-stone-900">
              {r.letters.split("").join(" ")}{suffix}
            </span>
            <span className="text-[11px] font-mono text-stone-600 tabular-nums">
              {r.points} pts{r.delta !== 0 ? ` (${r.delta > 0 ? "+" : ""}${r.delta})` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

